import {useTranslation} from '@aboutyou/i18n';
import {emailRegex} from '@aboutyou/utils';
import {styled} from '@linaria/react';
import React, {
  ChangeEvent,
  FC,
  RefObject,
  useCallback,
  useEffect,
  useState,
} from 'react';
import {useForm, useWatch} from 'react-hook-form';
import {COLORS} from '../../../constants/colors';
import {Button} from '../../atoms/Buttons/Button';
import {Icon} from '../../atoms/Icon/Icon';
import {GenericIconName} from '../../atoms/Icon/IconName';
import {Text} from '../../atoms/Text/Text';
import {TextInput} from '../../atoms/TextInput/TextInput';
import {useShowToast} from '../../atoms/Toast/hooks/useShowToast';
import {ToggleButton} from '../../atoms/ToggleButton/ToggleButton';

interface FormData {
  email: string;
}

export type FunctionHandleSizeSubmission = (
  formData: FormData,
  reset: () => void,
) => void;

interface SizeSubmissionProps {
  ref?: RefObject<HTMLFormElement>;
  email?: string;
  onBackButtonClick: VoidFunction;
  onSubmit: FunctionHandleSizeSubmission;
  onPushNotificationToggle: (event: ChangeEvent) => void;
  onEmailToggle: (event: ChangeEvent) => void;
  emailChecked: boolean;
  pushNotificationChecked: boolean;
  pushNotificationsEnabled: boolean;
  isPushNotificationBlocked: boolean;
  isPushNotificationPromptIgnored: boolean;
  hasBackButton?: boolean;
}

export const SizeSubmission: FC<
  React.PropsWithChildren<SizeSubmissionProps>
> = ({
  email,
  onBackButtonClick,
  onSubmit,
  children,
  onEmailToggle,
  onPushNotificationToggle,
  emailChecked,
  pushNotificationChecked,
  pushNotificationsEnabled,
  isPushNotificationBlocked,
  isPushNotificationPromptIgnored,
  hasBackButton = true,
  ref,
}) => {
  const showToast = useShowToast();
  const {t} = useTranslation();
  const submitButtonLabel = hasBackButton
    ? t('Notify')
    : t('Remind me when this is available');

  const {
    register,
    handleSubmit,
    control,
    formState: {errors, isSubmitSuccessful},
    reset,
  } = useForm<FormData>({
    defaultValues: {email},
    mode: 'onSubmit',
  });

  const [newUserFormInstanceKey, setNewUserFormInstanceKey] = useState(0);

  const resetFormState = useCallback(() => {
    reset();
    setNewUserFormInstanceKey(newUserFormInstanceKey + 1);
  }, [newUserFormInstanceKey, reset]);

  const emailFormValue = useWatch({
    control,
    name: 'email',
  });

  const submitHandler = (data: FormData) => {
    onSubmit(data, resetFormState);
  };

  const onEmailToggleChange = (event: ChangeEvent) => {
    if (emailChecked) {
      reset();
    }

    onEmailToggle(event);
  };

  const handleOnBackButtonClick = (
    e: React.MouseEvent<Element, MouseEvent>,
  ) => {
    e.preventDefault();
    onBackButtonClick();
  };

  useEffect(() => {
    if (isSubmitSuccessful) {
      reset({});
    }
  }, [isSubmitSuccessful]);

  useEffect(() => {
    if (pushNotificationsEnabled && !isPushNotificationBlocked) {
      showToast({
        content: t(
          'Benefit from all of our push notifications on sales & discounts, order & delivery status, and product availability updates. You can manage push notifications in your browser settings at any time.',
        ),
        type: 'INFO',
      });
    }
  }, [pushNotificationsEnabled, isPushNotificationBlocked]);

  // eslint-disable-next-line local-rules/too-complex-useeffects
  useEffect(() => {
    if (
      pushNotificationsEnabled &&
      (isPushNotificationBlocked || isPushNotificationPromptIgnored)
    ) {
      showToast({
        content: isPushNotificationPromptIgnored
          ? t(
              'You need to accept push notifications in your browser in order to receive them. Try again, or just sign-in for our e-mail notifications.',
            )
          : t(
              'You need to accept push notifications in your browser in order to receive them. Please enable push notifications in your browser settings, or just sign-in for e-mail notifications.',
            ),
        type: 'ERROR',
        'data-testid': 'sizeSubmissionPushNotificationErrorBlock',
      });
    }
  }, [
    pushNotificationsEnabled,
    isPushNotificationBlocked,
    isPushNotificationPromptIgnored,
  ]);

  return (
    <>
      {children}

      <form
        ref={ref}
        autoComplete="on"
        data-testid="sizeSubmission"
        onSubmit={handleSubmit(submitHandler)}
        method="POST"
        key={newUserFormInstanceKey}
      >
        <InputWrapper>
          {pushNotificationsEnabled && (
            <ToggleWrapper>
              <ToggleButton
                data-testid={'pushNotificationSizeSubmissionToggle'}
                onToggleClicked={onPushNotificationToggle}
                checked={pushNotificationChecked}
                disabled={isPushNotificationBlocked}
                aria-label={
                  pushNotificationChecked
                    ? t('Reject push notifications')
                    : t('Allow push notifications')
                }
              >
                {t('Allow push notifications')}
              </ToggleButton>
            </ToggleWrapper>
          )}

          <TextInputWrapper>
            {pushNotificationsEnabled && (
              <ToggleButton
                data-testid={'emailSizeSubmissionToggle'}
                onToggleClicked={onEmailToggleChange}
                checked={emailChecked}
                aria-label={
                  emailChecked
                    ? t('Deactivate e-mail notifications')
                    : t('Activate e-mail notifications')
                }
              >
                {t('Activate e-mail notifications')}
              </ToggleButton>
            )}

            <TextInput
              activeStyle={{
                backgroundColor: COLORS.White,
                borderColor: COLORS.Black,
              }}
              disabled={pushNotificationsEnabled && !emailChecked}
              autoComplete="email"
              data-testid="sizeReminderEmailInput"
              errorMessage={errors.email?.message}
              label={t('Your email address')}
              type="email"
              value={emailFormValue}
              {...register('email', {
                validate: {
                  required: v => {
                    if (pushNotificationsEnabled && emailChecked) {
                      return v === ''
                        ? t('Please enter your email address')
                        : true;
                    } else if (!pushNotificationsEnabled) {
                      return v === ''
                        ? t('Please enter your email address')
                        : true;
                    } else {
                      return true;
                    }
                  },
                },
                pattern: {
                  value: emailRegex,
                  message: t('This is not a valid email address'),
                },
              })}
            />
          </TextInputWrapper>
        </InputWrapper>

        <Footer $hasBackButton={hasBackButton}>
          {hasBackButton && (
            <BackButton
              data-testid="sizeReminderBackButton"
              onClick={handleOnBackButtonClick}
              variant="secondary"
            >
              {t('Back')}
            </BackButton>
          )}
          <SubmitButton
            data-testid="sizeReminderNotifyButton"
            htmlType="submit"
            disabled={
              pushNotificationsEnabled &&
              !pushNotificationChecked &&
              !emailChecked
            }
            aria-label={undefined}
          >
            <Icon fill={COLORS.White} asset={GenericIconName.Mail} size="SS" />
            <Text color="White">{submitButtonLabel}</Text>
          </SubmitButton>
        </Footer>
      </form>
    </>
  );
};

const InputWrapper = styled.div<{$hasBackButton?: boolean}>`
  margin-bottom: 20px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const ToggleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  border-bottom: 1px solid ${COLORS.Grey3};
  padding-bottom: 20px;
`;

const TextInputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Footer = styled.footer<{$hasBackButton?: boolean}>`
  box-sizing: border-box;
  padding-top: 20px;
  width: 100%;
  border-top: 1px solid ${COLORS.Grey3};
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: 0.75rem;
`;

const SubmitButton = styled(Button)`
  flex: 1 1 65%;
`;

const BackButton = styled(Button)`
  flex: 1 1 35%;
`;
